import React, { useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { strategySeverityScore } from '../utils/limitStatus';
import StrategyBubble from './StrategyBubble';
import { sectionCaptionSx, sectionTitleSx } from './dashboardStyles';

export default function StrategyRiskSection({ density = 'comfortable', onDensityChange, attentionItems = [], strategies = [], asOfDate, viewMode = 'threshold', thresholds = [], aum, displayMode = 'millions', portfolioGreeks = null, onSelectTrend }) {
  const strategyOrder = [
    'MERGER ARBITRAGE',
    'EDFI',
    'SPAC LIQUIDATION ARBITRAGE',
    'CONVERTIBLE ARBITRAGE',
    'CREDIT ARBITRAGE',
    'VOL ARBITRAGE',
    'STAT ARBITRAGE',
  ];

  const thresholdsByType = Object.fromEntries(
    thresholds.map((t) => [t.strategy_type, t])
  );

  // Standard order (fixed strategy order) by default; "Worst first" sorts by limit severity.
  const [sortMode, setSortMode] = useState('standard');
  const sortedStrategies = useMemo(
    () =>
      [...strategies].sort((a, b) => {
        const orderDiff = strategyOrder.indexOf(a.strategy_type_name) - strategyOrder.indexOf(b.strategy_type_name);
        if (sortMode !== 'severity') return orderDiff;
        return (
          strategySeverityScore(attentionItems, b.strategy_type_name) -
            strategySeverityScore(attentionItems, a.strategy_type_name) || orderDiff
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [strategies, attentionItems, sortMode],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={sectionTitleSx}>Strategy Limits &amp; Positions</Typography>
          <Typography sx={sectionCaptionSx}>Exposure, utilisation and VaR by strategy</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {onDensityChange && (
          <ToggleButtonGroup
            value={density}
            exclusive
            size="small"
            onChange={(e, next) => next && onDensityChange(next)}
            aria-label="Card density"
          >
            <ToggleButton value="comfortable" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>
              Comfortable
            </ToggleButton>
            <ToggleButton value="compact" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>
              Compact
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <ToggleButtonGroup
          value={sortMode}
          exclusive
          size="small"
          onChange={(e, next) => next && setSortMode(next)}
          aria-label="Strategy card order"
        >
          <ToggleButton value="standard" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>
            Standard order
          </ToggleButton>
          <ToggleButton value="severity" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>
            Worst first
          </ToggleButton>
        </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1fr 1fr 1fr',
          },
          columnGap: 2,
          rowGap: 0,
        }}
      >
        {sortedStrategies.map((strategy) => {
          const thresholdConfig = thresholdsByType[strategy.strategy_type_name];
          const drivingMeasure = thresholdConfig?.driving_measure || 'GMV';
          return (
            <StrategyBubble
              key={strategy.strategy_type_name}
              strategy={strategy}
              asOfDate={asOfDate}
              viewMode={viewMode}
              drivingMeasure={drivingMeasure}
              displayMode={displayMode}
              aum={aum}
              portfolioGreeks={portfolioGreeks}
              onSelectTrend={onSelectTrend}
              density={density}
            />
          );
        })}
      </Box>
    </Box>
  );
}
